/**
 * cockpit_zoom — the continuous scroll-wheel camera zoom for ship flight (space
 * + atmosphere). Pure + deterministic so it can be unit-tested without a boot.
 *
 * The flight camera is a rig-local child (the ship noses −Z, so +Z is behind).
 * A single scroll-driven `camBack` distance maps to the camera transform:
 *   · camBack ≤ ZOOM_COCKPIT   → FIRST-PERSON cockpit: sit at the eye, look down
 *                                the nose (the 2D canopy / the Sovereign's 3D
 *                                captain's chair engages here).
 *   · camBack > ZOOM_COCKPIT   → third-person CHASE whose height + downward pitch
 *                                grow with distance, out to ZOOM_MAX for a wide
 *                                situational view of the space around you.
 * Scrolling IN lowers camBack (toward the cockpit); scrolling OUT raises it.
 */

export interface ZoomCam {
  x: number; y: number; z: number; // rig-local camera position
  pitch: number;                   // rig-local X rotation (rad; negative = look down)
  cockpit: boolean;                // true ⇒ first-person cockpit engaged
}

export const ZOOM_COCKPIT = 3.2;   // camBack at/below this → first-person cockpit
export const ZOOM_CHASE = 15;      // the default third-person chase distance
export const ZOOM_MIN = -2;        // fully zoomed in (well inside the cockpit band)
export const ZOOM_MAX = 170;       // furthest pull-back
export const ZOOM_WHEEL_SENS = 0.06; // camBack change per wheel deltaY unit

/**
 * Rig-local camera transform for a scroll `camBack`, given the chassis's
 * first-person `eye` offset (cockpitEye). Cockpit engages at/below ZOOM_COCKPIT.
 */
export function zoomCamera(camBack: number, eye: { y: number; z: number }): ZoomCam {
  if (camBack <= ZOOM_COCKPIT) {
    return { x: 0, y: eye.y, z: eye.z, pitch: 0, cockpit: true };
  }
  const t = Math.min(1, (camBack - ZOOM_COCKPIT) / (ZOOM_MAX - ZOOM_COCKPIT)); // 0..1
  return {
    x: 0,
    y: 3.4 + t * 27,             // camera rises as it pulls back (top-down-ish when far)
    z: camBack,
    pitch: -0.06 - t * 0.24,     // and tips down to keep the ship framed
    cockpit: false,
  };
}

/** Clamp a scroll-adjusted back-distance to the allowed range. */
export function clampZoom(v: number): number {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v));
}

/** Apply a wheel deltaY to a back-distance (scroll up/in lowers it). */
export function zoomStep(camBack: number, deltaY: number): number {
  return clampZoom(camBack + deltaY * ZOOM_WHEEL_SENS);
}
